import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Icon } from "@/components/Icon";
import { Select } from "@/components/Select";
import { useTranslation } from "react-i18next";

export interface TabItem<T extends string = string> {
    id: T;
    label: string;
    icon?: string | React.ReactNode;
    badge?: number | string;
    disabled?: boolean;
    variant?: "default" | "primary";
    onClick?: () => void;
    className?: string;
}

export interface TabsProps<T extends string = string> {
    tabs?: TabItem<T>[];
    items?: TabItem<T>[];
    activeTab: T;
    onChange: (tabId: T) => void;
    variant?: "grid" | "pills" | "underline" | null;
    scroll?: boolean;
    subtabs?: boolean;
    actions?: boolean;
    className?: string;
    activeBgClass?: string;
    activeTextClass?: string;
    syncUrl?: boolean;
    urlPrefix?: string;
    urlDefaultTab?: T;
    full?: boolean;
    centered?: boolean;
    size?: "xs" | "sm" | "md" | "lg" | "xl";
    color?:
        | "primary"
        | "white"
        | "slate"
        | "zinc"
        | "rose"
        | "red"
        | "emerald"
        | "blue"
        | "indigo"
        | "purple"
        | "amber"
        | "warning"
        | "danger"
        | "success";
    fit?: boolean;
}

export const Tabs = <T extends string = string>({
    tabs: propTabs,
    items: propItems,
    activeTab,
    onChange,
    variant = "underline",
    scroll = true,
    subtabs = false,
    actions = false,
    className = "",
    activeBgClass = "bg-primary",
    activeTextClass = "text-white",
    syncUrl = true,
    urlPrefix,
    urlDefaultTab,
    full = false,
    centered = false,
    size = "md",
    color = "primary",
    fit = false,
}: TabsProps<T>): React.ReactElement | null => {
    const tabs: TabItem<T>[] = Array.isArray(propTabs)
        ? propTabs
        : Array.isArray(propItems)
          ? propItems
          : [];
    const { t } = useTranslation("global");
    const tabIdsKey = tabs.map((tab) => tab.id).join("|");
    const containerRef = useRef<HTMLDivElement>(null);

    let resolvedActiveBgClass = activeBgClass;
    let resolvedActiveTextClass = activeTextClass;
    let resolvedBorderColorClass = "border-primary";
    let resolvedTextColorClass = "text-primary";
    let resolvedUnderlineBgClass = "bg-primary/15 dark:bg-white";
    let resolvedContainerBgClass = "bg-box";
    let resolvedContainerBorderClass = "border-box dark:!border-white/25";
    let resolvedActiveBadgeBgClass = "bg-white shadow-2xs";

    if (color && color !== "primary") {
        resolvedActiveTextClass = "text-white";
        switch (color) {
            case "slate":
                resolvedActiveBgClass = "bg-slate-600";
                resolvedBorderColorClass = "border-slate-600";
                resolvedTextColorClass = "text-slate-600";
                resolvedUnderlineBgClass =
                    "bg-slate-600/15 dark:bg-slate-400/20";
                break;
            case "white":
                resolvedActiveBgClass = "bg-white dark:bg-zinc-800";
                resolvedActiveTextClass = "text-slate-900 dark:text-zinc-100";
                resolvedBorderColorClass =
                    "border-slate-300 dark:border-zinc-600";
                resolvedTextColorClass = "text-slate-900 dark:text-zinc-100";
                resolvedUnderlineBgClass =
                    "bg-slate-200/50 dark:bg-zinc-800/50";
                resolvedContainerBgClass =
                    "bg-slate-100 dark:bg-dark-subtle/50";
                resolvedContainerBorderClass =
                    "border-slate-200 dark:border-white/10";
                resolvedActiveBadgeBgClass =
                    "bg-slate-100 dark:bg-zinc-700 ring-1 ring-slate-200/60 dark:ring-white/10 shadow-sm";
                break;
            case "zinc":
                resolvedActiveBgClass = "bg-zinc-600";
                resolvedBorderColorClass = "border-zinc-600";
                resolvedTextColorClass = "text-zinc-600";
                resolvedUnderlineBgClass = "bg-zinc-600/15 dark:bg-zinc-400/20";
                break;
            case "red":
                resolvedActiveBgClass = "bg-red-500";
                resolvedBorderColorClass = "border-red-500";
                resolvedTextColorClass = "text-red-600 dark:text-red-400";
                resolvedUnderlineBgClass = "bg-red-500/15 dark:bg-red-500/20";
                break;
            case "rose":
            case "danger":
                resolvedActiveBgClass = "bg-rose-500";
                resolvedBorderColorClass = "border-rose-500";
                resolvedTextColorClass = "text-rose-600 dark:text-rose-400";
                resolvedUnderlineBgClass = "bg-rose-500/15 dark:bg-rose-500/20";
                break;
            case "emerald":
            case "success":
                resolvedActiveBgClass = "bg-emerald-500";
                resolvedBorderColorClass = "border-emerald-500";
                resolvedTextColorClass =
                    "text-emerald-600 dark:text-emerald-400";
                resolvedUnderlineBgClass =
                    "bg-emerald-500/15 dark:bg-emerald-500/20";
                break;
            case "blue":
                resolvedActiveBgClass = "bg-blue-500";
                resolvedBorderColorClass = "border-blue-500";
                resolvedTextColorClass = "text-blue-600 dark:text-blue-400";
                resolvedUnderlineBgClass = "bg-blue-500/15 dark:bg-blue-500/20";
                break;
            case "indigo":
                resolvedActiveBgClass = "bg-indigo-500";
                resolvedBorderColorClass = "border-indigo-500";
                resolvedTextColorClass = "text-indigo-600 dark:text-indigo-400";
                resolvedUnderlineBgClass =
                    "bg-indigo-500/15 dark:bg-indigo-500/20";
                break;
            case "purple":
                resolvedActiveBgClass = "bg-purple-500";
                resolvedBorderColorClass = "border-purple-500";
                resolvedTextColorClass = "text-purple-600 dark:text-purple-400";
                resolvedUnderlineBgClass =
                    "bg-purple-500/15 dark:bg-purple-500/20";
                break;
            case "amber":
            case "warning":
                resolvedActiveBgClass = "bg-amber-500";
                resolvedBorderColorClass = "border-amber-500";
                resolvedTextColorClass = "text-amber-600 dark:text-amber-400";
                resolvedUnderlineBgClass =
                    "bg-amber-500/15 dark:bg-amber-500/20";
                break;
        }
    }
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const tabButtonsRef = useRef<Map<string, HTMLButtonElement>>(new Map());
    const [useSelect, setUseSelect] = useState<boolean>(false);
    const [canScrollLeft, setCanScrollLeft] = useState<boolean>(false);
    const [canScrollRight, setCanScrollRight] = useState<boolean>(false);
    const [indicatorStyle, setIndicatorStyle] = useState<{
        left: number;
        top: number;
        width: number;
        height: number;
        ready: boolean;
    }>({
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        ready: false,
    });

    const checkScrollArrows = (): void => {
        const el = scrollContainerRef.current;
        if (!el || !scroll) {
            setCanScrollLeft(false);
            setCanScrollRight(false);
            return;
        }
        // Use a 12px tolerance because fractional flex calculations + paddings
        // can sometimes make scrollWidth slightly larger than clientWidth in WebKit/Blink
        const hasOverflow = el.scrollWidth > el.clientWidth + 12;
        setCanScrollLeft(hasOverflow && el.scrollLeft > 8);
        setCanScrollRight(
            hasOverflow &&
                Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth - 12
        );
    };

    useEffect(() => {
        if (!syncUrl || typeof window === "undefined" || tabs.length === 0) {
            return;
        }

        const resolveCurrentTabFromUrl = (): void => {
            const currentPath = window.location.pathname.replace(/\/+$/, "");
            const segments = currentPath
                .split("/")
                .map((segment) => decodeURIComponent(segment));
            const prefix = urlPrefix ? urlPrefix.replace(/\/+$/, "") : "";
            const searchParams = new URLSearchParams(window.location.search);
            const paramKey = subtabs ? "subtab" : "tab";
            const queryTab = searchParams.get(paramKey);

            if (queryTab) {
                const matched = tabs.find((item) => item.id === queryTab);
                if (matched) {
                    if (matched.id !== activeTab) {
                        onChange(matched.id);
                    }
                    return;
                }
            }

            if (urlDefaultTab && prefix && currentPath === prefix) {
                if (activeTab !== urlDefaultTab) {
                    onChange(urlDefaultTab);
                }

                if (syncUrl) {
                    const defaultPath = `${prefix}/${urlDefaultTab}`;
                    if (window.location.pathname !== defaultPath) {
                        window.history.replaceState(
                            { ...window.history.state, tab: urlDefaultTab },
                            "",
                            `${defaultPath}${window.location.search}${window.location.hash}`
                        );
                    }
                }

                return;
            }

            if (
                prefix &&
                (currentPath === prefix || currentPath.startsWith(`${prefix}/`))
            ) {
                const rest = currentPath
                    .slice(prefix.length)
                    .replace(/^\/+/, "");
                const firstSeg = rest.split("/").filter(Boolean)[0] || "";
                const matched = tabs.find((item) => item.id === firstSeg);

                if (matched) {
                    if (matched.id !== activeTab) {
                        onChange(matched.id);
                    }
                    return;
                }

                if (urlDefaultTab && activeTab !== urlDefaultTab) {
                    onChange(urlDefaultTab);
                }
                return;
            }

            for (let i = segments.length - 1; i >= 0; i--) {
                const seg = segments[i];
                const matched = tabs.find((item) => item.id === seg);
                if (matched) {
                    if (matched.id !== activeTab) {
                        onChange(matched.id);
                    }
                    return;
                }
            }

            if (urlDefaultTab && activeTab !== urlDefaultTab && !queryTab) {
                onChange(urlDefaultTab);
            }
        };

        resolveCurrentTabFromUrl();

        window.addEventListener("popstate", resolveCurrentTabFromUrl);
        return () => {
            window.removeEventListener("popstate", resolveCurrentTabFromUrl);
        };
    }, [syncUrl, tabIdsKey, activeTab, urlPrefix, urlDefaultTab, subtabs]);

    const updateIndicator = (): void => {
        const el = tabButtonsRef.current.get(activeTab);
        const parent = el?.parentElement;
        if (el && parent) {
            setIndicatorStyle({
                left: el.offsetLeft,
                top: el.offsetTop,
                width: el.offsetWidth,
                height: el.offsetHeight,
                ready: true,
            });
        }
    };

    useLayoutEffect(() => {
        const checkFit = (): void => {
            if (!containerRef.current || !measureRef.current) {
                return;
            }
            // Always allow scroll — never fallback to select
            setUseSelect(false);
            checkScrollArrows();
        };

        checkFit();
        updateIndicator();
        checkScrollArrows();

        const observer = new ResizeObserver(() => {
            checkFit();
            updateIndicator();
            checkScrollArrows();
        });

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        if (scrollContainerRef.current) {
            observer.observe(scrollContainerRef.current);
        }
        window.addEventListener("resize", checkFit);
        return () => {
            observer.disconnect();
            window.removeEventListener("resize", checkFit);
        };
    }, [tabIdsKey, activeTab, tabs.map((tab) => tab.label).join("|"), scroll]);

    useLayoutEffect(() => {
        updateIndicator();
        checkScrollArrows();

        if (scroll && scrollContainerRef.current) {
            const el = tabButtonsRef.current.get(activeTab);
            const container = scrollContainerRef.current;
            if (
                el &&
                container &&
                container.scrollWidth > container.clientWidth
            ) {
                const targetScrollLeft =
                    el.offsetLeft +
                    el.offsetWidth / 2 -
                    container.clientWidth / 2;
                container.scrollTo({
                    left: Math.max(0, targetScrollLeft),
                    behavior: "smooth",
                });
            }
        }
    }, [activeTab, useSelect]);

    if (tabs.length === 0) {
        return null;
    }

    const handleTabClick = (tabId: T): void => {
        onChange(tabId);
        if (scroll && scrollContainerRef.current) {
            const el = tabButtonsRef.current.get(tabId);
            const container = scrollContainerRef.current;
            if (
                el &&
                container &&
                container.scrollWidth > container.clientWidth
            ) {
                const targetScrollLeft =
                    el.offsetLeft +
                    el.offsetWidth / 2 -
                    container.clientWidth / 2;
                container.scrollTo({
                    left: Math.max(0, targetScrollLeft),
                    behavior: "smooth",
                });
            }
        }

        if (syncUrl && typeof window !== "undefined") {
            if (urlPrefix) {
                const prefix = urlPrefix.replace(/\/+$/, "");
                const newPath = `${prefix}/${tabId}`;

                if (window.location.pathname !== newPath) {
                    window.history.pushState(
                        { ...window.history.state, tab: tabId },
                        "",
                        `${newPath}${window.location.search}${window.location.hash}`
                    );
                }
            } else {
                const currentPath = window.location.pathname.replace(
                    /\/+$/,
                    ""
                );
                const segments = currentPath.split("/");
                let matchedIndex = -1;

                for (let i = segments.length - 1; i >= 0; i--) {
                    if (tabs.some((item) => item.id === segments[i])) {
                        matchedIndex = i;
                        break;
                    }
                }

                if (matchedIndex !== -1) {
                    segments[matchedIndex] = tabId;
                    const newPath = segments.join("/");
                    if (window.location.pathname !== newPath) {
                        window.history.pushState(
                            { ...window.history.state, tab: tabId },
                            "",
                            `${newPath}${window.location.search}${window.location.hash}`
                        );
                    }
                } else {
                    const url = new URL(window.location.href);
                    const paramKey = subtabs ? "subtab" : "tab";
                    if (urlDefaultTab && tabId === urlDefaultTab) {
                        url.searchParams.delete(paramKey);
                    } else {
                        url.searchParams.set(paramKey, tabId);
                    }
                    const newUrl = url.pathname + url.search + url.hash;
                    if (
                        newUrl !==
                        window.location.pathname +
                            window.location.search +
                            window.location.hash
                    ) {
                        window.history.pushState(
                            { ...window.history.state, tab: tabId },
                            "",
                            newUrl
                        );
                    }
                }
            }
        }
    };

    const getIconSize = () => {
        switch (size) {
            case "xs":
                return 12;
            case "sm":
                return 14;
            case "md":
                return 16;
            case "lg":
                return 18;
            case "xl":
                return 20;
            default:
                return 16;
        }
    };

    const renderIcon = (icon: React.ReactNode) => {
        if (typeof icon === "string") {
            return <Icon value={icon} size={getIconSize()} />;
        }
        if (React.isValidElement(icon)) {
            const props = icon.props as Record<string, unknown>;
            if (props && props.size !== undefined) {
                return icon;
            }
            return React.cloneElement(icon, {
                size: getIconSize(),
            } as React.Attributes & { size?: number | string });
        }
        return icon;
    };

    const getBadgeClasses = () => {
        switch (size) {
            case "xs":
                return "text-[9px] h-[14px] min-w-[14px] px-1";
            case "sm":
                return "text-[10px] h-[16px] min-w-[16px] px-1";
            case "md":
                return "text-[11px] h-[18px] min-w-[18px] px-1.5";
            case "lg":
                return "text-xs h-[20px] min-w-[20px] px-1.5";
            case "xl":
                return "text-sm h-[24px] min-w-[24px] px-2";
            default:
                return "text-[11px] h-[18px] min-w-[18px] px-1.5";
        }
    };

    const renderTabButton = (
        tab: TabItem<T>,
        buttonClassName: string,
        onButtonClick?: () => void
    ): React.ReactNode => {
        const isActive = !actions && activeTab === tab.id;
        const isUnderline = subtabs || variant === "underline";

        return (
            <button
                key={tab.id}
                ref={(el) => {
                    if (el) {
                        tabButtonsRef.current.set(tab.id, el);
                    } else {
                        tabButtonsRef.current.delete(tab.id);
                    }
                }}
                type="button"
                role={actions ? undefined : "tab"}
                aria-selected={actions ? undefined : isActive}
                tabIndex={actions || isActive ? 0 : -1}
                onKeyDown={(event) => {
                    if (
                        actions ||
                        !["ArrowLeft", "ArrowRight", "Home", "End"].includes(
                            event.key
                        )
                    ) {
                        return;
                    }
                    event.preventDefault();
                    const available = tabs.filter((entry) => !entry.disabled);
                    const index = available.findIndex(
                        (entry) => entry.id === tab.id
                    );
                    const next =
                        event.key === "Home"
                            ? 0
                            : event.key === "End"
                              ? available.length - 1
                              : (index +
                                    (event.key === "ArrowRight" ? 1 : -1) +
                                    available.length) %
                                available.length;
                    const target = available[next];
                    if (target) {
                        handleTabClick(target.id);
                        tabButtonsRef.current.get(target.id)?.focus();
                    }
                }}
                disabled={tab.disabled}
                onClick={onButtonClick ?? (() => handleTabClick(tab.id))}
                className={`${buttonClassName} ${tab.disabled ? "opacity-40 cursor-not-allowed" : ""} ${tab.className || ""}`}
            >
                {tab.icon && (
                    <span className="flex-shrink-0 relative z-10 flex items-center justify-center">
                        {renderIcon(tab.icon)}
                    </span>
                )}
                <span className="relative z-10 flex items-center justify-center mt-[1px]">
                    {tab.label}
                </span>
                {tab.badge !== undefined && (
                    <span
                        className={`relative z-10 font-black rounded-full inline-flex items-center justify-center transition-all duration-200 ${getBadgeClasses()} ${
                            isActive
                                ? isUnderline
                                    ? `${resolvedUnderlineBgClass} ${resolvedTextColorClass}`
                                    : `${resolvedActiveBadgeBgClass} ${resolvedTextColorClass}`
                                : `bg-slate-200/80 text-slate-600 dark:bg-white dark:text-slate-900`
                        }`}
                    >
                        {tab.badge}
                    </span>
                )}
            </button>
        );
    };

    const renderSelect = (): React.ReactNode => (
        <Select<T>
            value={activeTab}
            onChange={handleTabClick}
            options={tabs
                .filter((tab) => !tab.disabled)
                .map((tab) => ({
                    value: tab.id,
                    label:
                        tab.badge !== undefined
                            ? `${tab.label} (${tab.badge})`
                            : tab.label,
                }))}
            className="w-full"
        />
    );

    const getUnderlineSizeClasses = () => {
        switch (size) {
            case "xs":
                return "pb-1.5 text-[10px] sm:text-xs";
            case "sm":
                return "pb-2 text-xs sm:text-[13px]";
            case "md":
                return "pb-3 text-xs sm:text-sm";
            case "lg":
                return "pb-3.5 text-sm sm:text-base";
            case "xl":
                return "pb-4 text-base sm:text-lg";
            default:
                return "pb-3 text-xs sm:text-sm";
        }
    };

    if ((!variant && subtabs) || variant === "underline") {
        const buttonClass = (tab: TabItem<T>) => {
            const isActive = activeTab === tab.id;
            return `flex items-center justify-center gap-2 font-bold border-b-2 transition-colors cursor-pointer whitespace-nowrap ${getUnderlineSizeClasses()} ${
                isActive
                    ? `border-transparent ${resolvedTextColorClass}`
                    : "border-transparent text-slate-500 dark:!text-white hover:text-slate-900 dark:hover:!text-white"
            } ${full ? "flex-1 w-full" : ""}`;
        };

        return (
            <div
                ref={containerRef}
                className={`relative ${fit ? "w-fit" : "w-full"} min-w-0 max-w-full ${className}`}
            >
                <div
                    ref={measureRef}
                    className={`invisible absolute h-0 overflow-hidden pointer-events-none flex items-center ${full ? "w-full gap-0" : "gap-6"} whitespace-nowrap`}
                    aria-hidden="true"
                >
                    {tabs.map((tab) => renderTabButton(tab, buttonClass(tab)))}
                </div>
                {useSelect ? (
                    renderSelect()
                ) : (
                    <div
                        ref={scrollContainerRef}
                        className="flex items-center border-b border-slate-200 dark:border-zinc-800 overflow-x-auto scrollbar-none w-full"
                    >
                        <div
                            role={actions ? undefined : "tablist"}
                            aria-label={t("workspace.screens")}
                            className={`relative flex items-center ${centered ? "mx-auto w-full max-w-2xl justify-between gap-8" : full ? "w-full gap-0" : "w-max gap-6"} min-w-0`}
                        >
                            {indicatorStyle.ready && (
                                <div
                                    className={`absolute bottom-0 left-0 h-0.5 ${resolvedActiveBgClass} pointer-events-none will-change-transform transition-[transform,width] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] z-10`}
                                    style={{
                                        transform: `translate3d(${indicatorStyle.left}px, 0, 0)`,
                                        width: `${indicatorStyle.width}px`,
                                    }}
                                />
                            )}
                            {tabs.map((tab) =>
                                renderTabButton(tab, buttonClass(tab))
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    const getPillsSizeClasses = () => {
        switch (size) {
            case "xs":
                return "py-1 px-3 text-[10px] sm:text-xs rounded-md";
            case "sm":
                return "py-1.5 px-3.5 text-xs sm:text-[13px] rounded-lg";
            case "md":
                return "py-2 sm:py-2.5 px-4 text-xs sm:text-sm rounded-xl";
            case "lg":
                return "py-2.5 sm:py-3 px-5 text-sm sm:text-base rounded-xl";
            case "xl":
                return "py-3 sm:py-3.5 px-6 text-base sm:text-lg rounded-2xl";
            default:
                return "py-2 sm:py-2.5 px-4 text-xs sm:text-sm rounded-xl";
        }
    };

    if (variant === "pills") {
        const buttonClass = (tab: TabItem<T>) => {
            const isActive = activeTab === tab.id;
            return `flex items-center justify-center gap-2 font-bold transition-all cursor-pointer whitespace-nowrap ${getPillsSizeClasses()} ${
                isActive
                    ? `${resolvedActiveBgClass} ${resolvedActiveTextClass} shadow-xs`
                    : "border border-slate-200 dark:border-zinc-800 text-slate-600 dark:!text-white hover:text-slate-900 hover:bg-slate-100 dark:hover:bg-zinc-800"
            } ${full ? "flex-1 w-full" : ""}`;
        };

        return (
            <div
                ref={containerRef}
                className={`relative ${fit ? "w-fit" : "w-full"} min-w-0 max-w-full overflow-hidden ${className}`}
            >
                <div
                    ref={measureRef}
                    className={`invisible absolute h-0 overflow-hidden pointer-events-none flex items-center ${full ? "w-full gap-2" : "gap-2"} whitespace-nowrap`}
                    aria-hidden="true"
                >
                    {tabs.map((tab) => renderTabButton(tab, buttonClass(tab)))}
                </div>
                {useSelect ? (
                    renderSelect()
                ) : (
                    <div
                        className={`flex items-center w-full ${scroll ? "overflow-x-auto scrollbar-none" : "flex-wrap"}`}
                    >
                        <div
                            role={actions ? undefined : "tablist"}
                            aria-label={t("workspace.screens")}
                            className={`flex items-center gap-2 ${full ? "w-full" : "w-max mx-auto"} min-w-0`}
                        >
                            {tabs.map((tab) =>
                                renderTabButton(tab, buttonClass(tab))
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // Default: Animated Grid / Smooth Sliding Indicator (as in jmpreparadorfisico)
    const gridColsClass =
        tabs.length === 2
            ? "grid-cols-2"
            : tabs.length === 3
              ? "grid-cols-3"
              : tabs.length === 4
                ? "grid-cols-2 sm:grid-cols-4"
                : tabs.length === 5
                  ? "grid-cols-2 sm:grid-cols-3 md:grid-cols-5"
                  : "grid-cols-2 sm:grid-cols-3 md:grid-cols-6";

    const getDefaultSizeClasses = () => {
        switch (size) {
            case "xs":
                return "py-1 sm:py-1 px-2 sm:px-3 text-[10px] sm:text-[11px] rounded-md";
            case "sm":
                return "py-1.5 sm:py-1.5 px-3 sm:px-4 text-[11px] sm:text-xs rounded-lg";
            case "md":
                return "py-2 sm:py-2 px-4 sm:px-5 text-xs sm:text-sm rounded-xl";
            case "lg":
                return "py-2.5 sm:py-2.5 px-5 sm:px-6 text-sm sm:text-base rounded-xl";
            case "xl":
                return "py-3 sm:py-3 px-6 sm:px-8 text-base sm:text-lg rounded-2xl";
            default:
                return "py-2 sm:py-2 px-4 sm:px-5 text-xs sm:text-sm rounded-xl";
        }
    };

    const buttonClass = (tab: TabItem<T>) => {
        const isActive = activeTab === tab.id;

        return `relative z-10 flex-1 min-w-fit flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap transition-colors duration-150 shrink-0 bg-transparent ${getDefaultSizeClasses()} ${
            isActive
                ? `${resolvedActiveTextClass} font-bold`
                : "text-slate-600 dark:!text-white font-semibold hover:text-slate-900 dark:hover:!text-white"
        }`;
    };

    return (
        <div
            ref={containerRef}
            className={`relative ${fit ? "w-fit" : "w-full"} min-w-0 max-w-full ${className}`}
        >
            <div
                ref={measureRef}
                className="invisible absolute h-0 overflow-hidden pointer-events-none inline-flex items-center gap-1 whitespace-nowrap"
                aria-hidden="true"
            >
                {tabs.map((tab) => renderTabButton(tab, buttonClass(tab)))}
            </div>
            {useSelect ? (
                renderSelect()
            ) : (
                <div className={`relative ${fit ? "w-fit" : "w-full"} min-w-0`}>
                    {canScrollLeft && (
                        <div className="pointer-events-none absolute left-[-8px] top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-5 h-5 rounded-full bg-white/95 dark:bg-zinc-800/95 shadow-2xs text-slate-400 dark:text-zinc-400 border border-slate-200/80 dark:border-zinc-700/80">
                            <Icon value="chevronLeft" size={14} />
                        </div>
                    )}

                    <div
                        ref={scrollContainerRef}
                        role={actions ? undefined : "tablist"}
                        aria-label={t("workspace.screens")}
                        onScroll={checkScrollArrows}
                        className={`relative ${fit ? "w-fit" : "w-full"} max-w-full shadow-2xs border ${resolvedContainerBgClass} ${resolvedContainerBorderClass} ${
                            size === "xs"
                                ? "rounded-lg p-0.5"
                                : size === "sm"
                                  ? "rounded-xl p-0.5"
                                  : "rounded-2xl p-1"
                        } overflow-x-auto scrollbar-none flex items-stretch gap-1 min-w-0`}
                    >
                        {indicatorStyle.ready && (
                            <div
                                className={`absolute top-0 left-0 ${size === "xs" ? "rounded-md" : size === "sm" ? "rounded-lg" : "rounded-xl"} ${resolvedActiveBgClass} shadow-xs pointer-events-none z-0 will-change-transform transition-[transform,width] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]`}
                                style={{
                                    transform: `translate3d(${indicatorStyle.left}px, ${indicatorStyle.top}px, 0)`,
                                    width: `${indicatorStyle.width}px`,
                                    height: `${indicatorStyle.height}px`,
                                }}
                            />
                        )}
                        {tabs.map((tab) =>
                            renderTabButton(tab, buttonClass(tab))
                        )}
                    </div>

                    {canScrollRight && (
                        <div className="pointer-events-none absolute right-[-8px] top-1/2 -translate-y-1/2 z-20 flex items-center justify-center w-5 h-5 rounded-full bg-white/95 shadow-2xs text-slate-400 border border-slate-200/80">
                            <Icon value="chevronRight" size={14} />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Tabs;
